import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Add, ChevronLeft, ChevronRight, DeleteOutline, EditOutlined, EmojiObjectsOutlined, Refresh } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const CHANNELS = [
  { id: "instagram", name: "Instagram", color: "#C13584", formats: ["Post", "Story", "Reel", "Carrusel"] },
  { id: "tiktok", name: "TikTok", color: "#1F2937", formats: ["Video", "Trend/Sonido"] },
  { id: "whatsapp", name: "Canal de WhatsApp", color: "#25D366", formats: ["Mensaje", "Promo/Combo", "Recordatorio"] },
];

const PILLARS = [
  { name: "Aprovechá tu pedido", color: "#2F6B4F" },
  { name: "De la chacra a tu puerta", color: "#8C6239" },
  { name: "Ofertas y combos", color: "#E8563A" },
  { name: "Trucos de cocina y despensa", color: "#F2B705" },
  { name: "Nuestra gente", color: "#C13584" },
  { name: "Llegó lo nuevo", color: "#2B6CB0" },
];

const DAY_NAMES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const STATUSES = [
  { value: "planificado", label: "Planificado", bg: "#ede9fe", color: "#5b21b6" },
  { value: "diseno", label: "En diseño", bg: "#fff1d6", color: "#9a5b00" },
  { value: "listo", label: "Listo", bg: "#dff4e8", color: "#17603c" },
  { value: "publicado", label: "Publicado", bg: "#dceeff", color: "#185a8d" },
];
const FORMAT_COLORS = {
  Reel: "#E8563A", Post: "#2F6B4F", Carrusel: "#F2B705", Story: "#C13584",
  Video: "#1F2937", "Trend/Sonido": "#6E7263", Mensaje: "#25D366",
  "Promo/Combo": "#E8563A", Recordatorio: "#2F6B4F",
};

const SEED_PLAN = {
  instagram: [
    { offset: 0, format: "Post", pillar: "Trucos de cocina y despensa", title: "Cómo elegir la fruta más madura: guía rápida", notes: "3 tips visuales (color, aroma, textura). Cerrar con CTA: ‘pedí ya y te la llevamos fresca’." },
    { offset: 1, format: "Reel", pillar: "De la chacra a tu puerta", title: "Así armamos tu bolsón de frutas y verduras", notes: "Mini making-of: selección, pesada y armado del bolsón. Música alegre y texto en pantalla." },
    { offset: 2, format: "Carrusel", pillar: "Aprovechá tu pedido", title: "3 recetas con lo que llegó esta semana", notes: "Tres slides, una receta rápida por slide, con ingredientes típicos del pedido." },
    { offset: 3, format: "Post", pillar: "Nuestra gente", title: "Lo que dicen nuestros clientes ❤️", notes: "Captura de una reseña real y foto de un pedido armado." },
    { offset: 4, format: "Reel", pillar: "Ofertas y combos", title: "Combo del finde: armá tu changuito", notes: "Reel rápido mostrando el combo con precio en pantalla y cuenta regresiva." },
    { offset: 5, format: "Post", pillar: "Llegó lo nuevo", title: "Ya podés pedir los productos de temporada", notes: "Foto del producto nuevo y copy corto anunciando que ya se puede pedir." },
    { offset: 6, format: "Carrusel", pillar: "Trucos de cocina y despensa", title: "Organizá tu heladera para arrancar la semana", notes: "Guía de organización con foto real usando lo que llega en el pedido semanal." },
  ],
  tiktok: [
    { offset: 0, format: "Video", pillar: "Trucos de cocina y despensa", title: "Truco para que la palta no se ponga negra", notes: "Video de 15 segundos, texto grande en pantalla y sonido del momento." },
    { offset: 2, format: "Video", pillar: "De la chacra a tu puerta", title: "Un día armando pedidos en el depósito", notes: "POV cámara en mano recorriendo el depósito mientras se arman pedidos." },
    { offset: 4, format: "Trend/Sonido", pillar: "Ofertas y combos", title: "Sumate al trend de ‘lo que compré esta semana’", notes: "Unboxing del combo del finde con el sonido que esté en tendencia." },
    { offset: 6, format: "Video", pillar: "Aprovechá tu pedido", title: "Receta exprés con lo que sobró", notes: "Formato antes/después: una receta en 20 segundos con sobras del pedido." },
  ],
  whatsapp: [
    { offset: 0, format: "Promo/Combo", pillar: "Ofertas y combos", title: "Arrancá la semana con descuento en frutas de estación", notes: "Arrancá la semana fresca 🍊 -15% en frutas de estación. Pedí antes de las 18 h." },
    { offset: 1, format: "Promo/Combo", pillar: "Ofertas y combos", title: "2x1 en verduras de hoja", notes: "2x1 en verduras de hoja seleccionadas, solo por hoy." },
    { offset: 2, format: "Promo/Combo", pillar: "Ofertas y combos", title: "Combo mitad de semana: bolsón chico + huevos", notes: "Bolsón chico y docena de huevos a precio especial." },
    { offset: 3, format: "Promo/Combo", pillar: "Ofertas y combos", title: "Almacén: llevá 3 y pagá 2", notes: "Promoción de almacén con stock limitado." },
    { offset: 4, format: "Promo/Combo", pillar: "Ofertas y combos", title: "Combo finde: fruta y verdura fresca", notes: "Combo armado para sábado y domingo. Pedí antes del mediodía." },
    { offset: 5, format: "Promo/Combo", pillar: "Ofertas y combos", title: "Promo fin de semana en congelados", notes: "Sábado de descuento en congelados." },
    { offset: 6, format: "Promo/Combo", pillar: "Ofertas y combos", title: "Descuento en pedidos para entrega el lunes", notes: "Dejá tu pedido hoy y recibilo a primera hora del lunes." },
  ],
};

const emptyForm = {
  id: null,
  day: "",
  title: "",
  format: "Post",
  pillar: PILLARS[0].name,
  time: "",
  status: "planificado",
  notes: "",
};

const useStyles = makeStyles((theme) => ({
  page: {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
    background: "#fbf8f0",
    color: "#23281f",
    padding: "22px clamp(12px, 3vw, 30px) 60px",
  },
  wrap: { maxWidth: 1500, margin: "0 auto" },
  hero: {
    position: "relative",
    overflow: "hidden",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 18,
    color: "#fff",
    borderRadius: 24,
    padding: "24px clamp(18px, 4vw, 38px)",
    background: "linear-gradient(120deg, #245b43 0%, #4f9a6e 48%, #e8563a 130%)",
    boxShadow: "0 18px 38px rgba(47,107,79,.22)",
    "&::after": {
      content: '\"\"', position: "absolute", width: 250, height: 250, borderRadius: "50%",
      right: -75, top: -125, background: "rgba(242,183,5,.22)",
    },
  },
  brand: { position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 14 },
  brandMark: {
    width: 58, height: 58, borderRadius: 18, display: "grid", placeItems: "center",
    fontSize: 30, background: "rgba(255,255,255,.18)", border: "1px solid rgba(255,255,255,.28)",
  },
  heroTitle: { fontWeight: 800, lineHeight: 1.05, letterSpacing: "-.025em" },
  heroSubtitle: { opacity: .88, marginTop: 5 },
  roleToggle: {
    position: "relative", zIndex: 1, display: "flex", gap: 4, padding: 4,
    background: "rgba(255,255,255,.16)", borderRadius: 14,
  },
  roleButton: {
    border: 0, borderRadius: 10, padding: "9px 13px", cursor: "pointer", color: "#fff",
    background: "transparent", fontWeight: 700, fontFamily: "inherit",
  },
  roleButtonActive: { color: "#245b43", background: "#fff", boxShadow: "0 5px 14px rgba(0,0,0,.12)" },
  viewerBanner: {
    marginTop: 14, borderRadius: 14, padding: "11px 14px", color: "#79520b",
    background: "#fff4d2", border: "1px solid #f2d98c", fontWeight: 600,
  },
  toolbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap",
    gap: 12, margin: "20px 0 14px",
  },
  weekNav: {
    display: "flex", alignItems: "center", gap: 8, minWidth: 285,
    background: "#fff", border: "1px solid #e6e1d2", borderRadius: 16, padding: "5px 8px",
    boxShadow: "0 8px 22px rgba(35,40,31,.06)",
  },
  weekLabel: { flex: 1, textAlign: "center", fontWeight: 800, fontSize: ".92rem" },
  channels: { display: "flex", flexWrap: "wrap", gap: 8 },
  channel: {
    border: "1px solid #ddd7c8", borderRadius: 999, padding: "9px 13px", cursor: "pointer",
    background: "#fff", color: "#4d5147", fontWeight: 700, fontFamily: "inherit",
  },
  dot: { display: "inline-block", width: 8, height: 8, borderRadius: "50%", marginRight: 7 },
  legend: {
    display: "flex", flexWrap: "wrap", gap: "8px 14px", alignItems: "center",
    padding: "12px 15px", background: "rgba(255,255,255,.74)", border: "1px solid #e6e1d2", borderRadius: 15,
  },
  legendItem: { fontSize: ".76rem", color: "#5e6358", fontWeight: 600 },
  seedRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, margin: "14px 0" },
  seedMessage: { color: "#2f6b4f", fontWeight: 700, fontSize: ".83rem" },
  scroll: { overflowX: "auto", paddingBottom: 8 },
  calendarGrid: {
    display: "grid", gridTemplateColumns: "repeat(7, minmax(184px, 1fr))", gap: 10, minWidth: 1340,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr", minWidth: 0 },
  },
  day: {
    display: "flex", flexDirection: "column", minHeight: 380, borderRadius: 18, overflow: "hidden",
    border: "1px solid #e6e1d2", background: "rgba(255,255,255,.8)",
  },
  today: { borderColor: "#f2b705", boxShadow: "0 0 0 2px rgba(242,183,5,.18)" },
  dayHead: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
    padding: "13px 13px 11px", background: "#fff", borderBottom: "1px solid #eee9dc",
  },
  dayName: { fontWeight: 800, fontSize: ".88rem" },
  dayDate: { color: "#7d8176", fontSize: ".76rem", fontWeight: 700 },
  missingStory: {
    margin: "8px 9px 0", padding: "6px 8px", borderRadius: 8, fontSize: ".68rem", fontWeight: 700,
    color: "#8a496e", background: "#faeaf3", border: "1px dashed #d99abc",
  },
  cards: { flex: 1, padding: 9 },
  card: {
    position: "relative", padding: "10px 9px 9px 11px", marginBottom: 8, borderRadius: 11,
    background: "#fff", border: "1px solid #ece7dc", borderLeftWidth: 4,
    boxShadow: "0 5px 14px rgba(35,40,31,.055)", cursor: "pointer",
    "&:hover": { transform: "translateY(-1px)", boxShadow: "0 8px 18px rgba(35,40,31,.09)" },
  },
  badges: { display: "flex", flexWrap: "wrap", gap: 4, paddingRight: 20 },
  formatBadge: { borderRadius: 999, color: "#fff", fontSize: ".61rem", padding: "3px 6px", fontWeight: 800 },
  pillarBadge: { border: "1px solid", borderRadius: 999, fontSize: ".59rem", padding: "2px 5px", fontWeight: 700 },
  statusBadge: { borderRadius: 999, fontSize: ".59rem", padding: "3px 6px", fontWeight: 800 },
  cardTitle: { marginTop: 8, fontWeight: 800, fontSize: ".82rem", lineHeight: 1.35, overflowWrap: "anywhere" },
  cardMeta: { marginTop: 5, fontSize: ".68rem", color: "#6e7263", lineHeight: 1.4, overflowWrap: "anywhere" },
  cardDelete: { position: "absolute", right: 1, top: 1, padding: 4 },
  empty: { textAlign: "center", color: "#a2a59b", fontSize: ".76rem", padding: "30px 5px" },
  addButton: {
    margin: "0 9px 9px", borderRadius: 10, border: "1px dashed #b9b4a6", color: "#5f6259",
    background: "transparent", padding: "8px", cursor: "pointer", fontWeight: 800, fontFamily: "inherit",
    "&:hover": { color: "#2f6b4f", borderColor: "#2f6b4f", background: "#edf6f0" },
  },
  sync: { marginTop: 13, color: "#74786c", fontSize: ".75rem", display: "flex", alignItems: "center" },
  syncDot: { width: 8, height: 8, borderRadius: "50%", background: "#43a16f", marginRight: 7, boxShadow: "0 0 0 4px rgba(67,161,111,.13)" },
  dialogPaper: { borderRadius: 20 },
  fields: { display: "grid", gap: 15, paddingTop: 4 },
}));

function getMonday(offset = 0) {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  const day = date.getDay();
  date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day) + offset * 7);
  return date;
}

function dateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function weekDays(offset) {
  const monday = getMonday(offset);
  return DAY_NAMES.map((name, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    return { name, date, key: dateKey(date) };
  });
}

function pillarColor(name) {
  return PILLARS.find((pillar) => pillar.name === name)?.color || "#6e7263";
}

function statusDetails(value) {
  return STATUSES.find((status) => status.value === value) || STATUSES[0];
}

function payloadFor(entry, channel) {
  return {
    channel,
    day: entry.day,
    title: entry.title,
    format: entry.format,
    pillar: entry.pillar,
    time: entry.time || "",
    status: entry.status || "planificado",
    notes: entry.notes || "",
  };
}

export default function ContentCalendar() {
  const classes = useStyles();
  const initialSeedStarted = useRef(false);
  const [entries, setEntries] = useState([]);
  const [channel, setChannel] = useState("instagram");
  const [weekOffset, setWeekOffset] = useState(0);
  const [viewer, setViewer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedMessage, setSeedMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const days = useMemo(() => weekDays(weekOffset), [weekOffset]);
  const activeChannel = CHANNELS.find((item) => item.id === channel) || CHANNELS[0];
  const startKey = days[0].key;
  const endKey = days[6].key;
  const visibleEntries = entries.filter((entry) => entry.channel === channel && entry.day >= startKey && entry.day <= endKey);

  const loadEntries = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const { data } = await api.get("/crm/content-calendar/", { params: { pageSize: 1000 } });
      const nextEntries = data.entries || [];
      setEntries(nextEntries);
      return nextEntries;
    } catch (error) {
      if (!quiet) toastError(error);
      return [];
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  const seedWeek = useCallback(async (offset, currentEntries, silent = false) => {
    const monday = getMonday(offset);
    let added = 0;
    setSeeding(true);
    try {
      for (const targetChannel of CHANNELS) {
        for (const idea of SEED_PLAN[targetChannel.id] || []) {
          const date = new Date(monday);
          date.setDate(monday.getDate() + idea.offset);
          const day = dateKey(date);
          const alreadyHasContent = currentEntries.some((entry) => entry.channel === targetChannel.id && entry.day === day);
          if (alreadyHasContent) continue;
          const { data } = await api.post("/crm/content-calendar", payloadFor({ ...idea, day }, targetChannel.id));
          currentEntries = [data, ...currentEntries];
          added += 1;
        }
      }
      setEntries(currentEntries);
      if (!silent) {
        const message = added
          ? `Se agregaron ${added} ideas nuevas. No se modificaron tus contenidos ni las Stories.`
          : "La semana ya tiene contenido cargado en todos los días previstos.";
        setSeedMessage(message);
        toast.success(message);
      }
      return currentEntries;
    } catch (error) {
      toastError(error);
      return currentEntries;
    } finally {
      setSeeding(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const initialize = async () => {
      const current = await loadEntries();
      if (mounted && !initialSeedStarted.current) {
        initialSeedStarted.current = true;
        await seedWeek(0, current, true);
      }
    };
    initialize();
    const timer = setInterval(() => loadEntries({ quiet: true }), 8000);
    return () => { mounted = false; clearInterval(timer); };
  }, [loadEntries, seedWeek]);

  const changeChannel = (nextChannel) => {
    setChannel(nextChannel.id);
    setSeedMessage("");
  };

  const openEditor = (day, entry = null) => {
    const formats = activeChannel.formats;
    setForm(entry ? { ...emptyForm, ...entry } : { ...emptyForm, day, format: formats[0] });
    setDialogOpen(true);
  };

  const saveEntry = async () => {
    if (!form.title.trim()) {
      toast.warn("Escribí un título o una idea para el contenido.");
      return;
    }
    try {
      const request = form.id
        ? api.put(`/crm/content-calendar/${form.id}`, payloadFor(form, channel))
        : api.post("/crm/content-calendar", payloadFor(form, channel));
      await request;
      setDialogOpen(false);
      await loadEntries({ quiet: true });
      toast.success(form.id ? "Contenido actualizado." : "Contenido agregado al calendario.");
    } catch (error) {
      toastError(error);
    }
  };

  const removeEntry = async (entry) => {
    if (!window.confirm(`¿Eliminar “${entry.title}” del calendario?`)) return;
    try {
      await api.delete(`/crm/content-calendar/${entry.id}`);
      setEntries((current) => current.filter((item) => item.id !== entry.id));
      toast.success("Contenido eliminado.");
    } catch (error) {
      toastError(error);
    }
  };

  const weekLabel = `${days[0].date.toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit" })} — ${days[6].date.toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit" })}${weekOffset === 0 ? " · Esta semana" : ""}`;
  const today = dateKey(new Date());

  return (
    <MainContainer>
      <div className={classes.page}>
        <div className={classes.wrap}>
          <section className={classes.hero}>
            <div className={classes.brand}>
              <div className={classes.brandMark}>🌿</div>
              <div>
                <Typography variant="h4" className={classes.heroTitle}>La Buena Vibra</Typography>
                <Typography className={classes.heroSubtitle}>Planificación semanal de redes sociales</Typography>
              </div>
            </div>
            <div className={classes.roleToggle} aria-label="Modo del calendario">
              <button type="button" onClick={() => setViewer(false)} className={`${classes.roleButton} ${!viewer ? classes.roleButtonActive : ""}`}>Vero (edita)</button>
              <button type="button" onClick={() => setViewer(true)} className={`${classes.roleButton} ${viewer ? classes.roleButtonActive : ""}`}>Kevin (mira)</button>
            </div>
          </section>

          {viewer && <div className={classes.viewerBanner}>Estás viendo el calendario en modo solo lectura, como lo ve Kevin.</div>}

          <div className={classes.toolbar}>
            <div className={classes.weekNav}>
              <IconButton size="small" aria-label="Semana anterior" onClick={() => setWeekOffset((value) => value - 1)}><ChevronLeft /></IconButton>
              <div className={classes.weekLabel}>{weekLabel}</div>
              <IconButton size="small" aria-label="Semana siguiente" onClick={() => setWeekOffset((value) => value + 1)}><ChevronRight /></IconButton>
            </div>
            <div className={classes.channels}>
              {CHANNELS.map((item) => {
                const selected = item.id === channel;
                return <button
                  type="button"
                  key={item.id}
                  className={classes.channel}
                  onClick={() => changeChannel(item)}
                  style={selected ? { background: item.color, borderColor: item.color, color: "#fff", boxShadow: `0 7px 18px ${item.color}33` } : undefined}
                ><span className={classes.dot} style={{ background: selected ? "#fff" : item.color }} />{item.name}</button>;
              })}
            </div>
          </div>

          <div className={classes.legend}>
            {PILLARS.map((pillar) => <span className={classes.legendItem} key={pillar.name}><span className={classes.dot} style={{ background: pillar.color }} />{pillar.name}</span>)}
          </div>

          {!viewer && <div className={classes.seedRow}>
            <Button
              variant="contained"
              color="primary"
              startIcon={seeding ? <Refresh /> : <EmojiObjectsOutlined />}
              disabled={seeding}
              onClick={() => seedWeek(weekOffset, [...entries])}
            >{seeding ? "Cargando ideas..." : "Cargar propuesta de contenidos"}</Button>
            {seedMessage && <span className={classes.seedMessage}>{seedMessage}</span>}
          </div>}

          <div className={classes.scroll}>
            <div className={classes.calendarGrid}>
              {days.map((day) => {
                const dayEntries = visibleEntries.filter((entry) => entry.day === day.key);
                const missingStory = channel === "instagram" && !dayEntries.some((entry) => entry.format === "Story");
                return <Paper elevation={0} className={`${classes.day} ${day.key === today ? classes.today : ""}`} key={day.key}>
                  <div className={classes.dayHead}>
                    <span className={classes.dayName}>{day.name}</span>
                    <span className={classes.dayDate}>{day.date.toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit" })}</span>
                  </div>
                  {missingStory && <div className={classes.missingStory}>{viewer ? "Sin historia hoy" : "⚠ Falta cargar historia"}</div>}
                  <div className={classes.cards}>
                    {!dayEntries.length && <div className={classes.empty}>{loading ? "Cargando..." : "Sin contenido"}</div>}
                    {dayEntries.map((entry) => {
                      const formatColor = FORMAT_COLORS[entry.format] || "#6e7263";
                      const status = statusDetails(entry.status);
                      return <div
                        className={classes.card}
                        style={{ borderLeftColor: formatColor }}
                        key={entry.id}
                        onClick={() => !viewer && openEditor(day.key, entry)}
                        role={!viewer ? "button" : undefined}
                        tabIndex={!viewer ? 0 : undefined}
                      >
                        {!viewer && <Tooltip title="Eliminar">
                          <IconButton className={classes.cardDelete} size="small" aria-label="Eliminar contenido" onClick={(event) => { event.stopPropagation(); removeEntry(entry); }}><DeleteOutline fontSize="small" /></IconButton>
                        </Tooltip>}
                        <div className={classes.badges}>
                          <span className={classes.formatBadge} style={{ background: formatColor }}>{entry.format}</span>
                          {entry.pillar && <span className={classes.pillarBadge} style={{ color: pillarColor(entry.pillar), borderColor: pillarColor(entry.pillar) }}>{entry.pillar}</span>}
                          <span className={classes.statusBadge} style={{ color: status.color, background: status.bg }}>{status.label}</span>
                        </div>
                        <div className={classes.cardTitle}>{entry.title}</div>
                        {entry.time && <div className={classes.cardMeta}>🕐 {entry.time}</div>}
                        {entry.notes && <div className={classes.cardMeta}>{entry.notes}</div>}
                        {!viewer && <div className={classes.cardMeta}><EditOutlined style={{ fontSize: 13, verticalAlign: "middle" }} /> Tocá para editar</div>}
                      </div>;
                    })}
                  </div>
                  {!viewer && <button type="button" className={classes.addButton} onClick={() => openEditor(day.key)}><Add style={{ fontSize: 16, verticalAlign: "middle" }} /> contenido</button>}
                </Paper>;
              })}
            </div>
          </div>

          <div className={classes.sync}><span className={classes.syncDot} />Se actualiza automáticamente para todos los usuarios del CRM.</div>
        </div>
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ className: classes.dialogPaper }}>
        <DialogTitle>{form.id ? "Editar contenido" : "Nuevo contenido"}</DialogTitle>
        <DialogContent dividers>
          <div className={classes.fields}>
            <TextField autoFocus required fullWidth label="Título / idea" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="Ej: Reel con tips de la semana" />
            <TextField select fullWidth label="Formato" value={form.format} onChange={(event) => setForm({ ...form, format: event.target.value })}>
              {activeChannel.formats.map((format) => <MenuItem value={format} key={format}>{format}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Pilar de contenido" value={form.pillar} onChange={(event) => setForm({ ...form, pillar: event.target.value })}>
              {PILLARS.map((pillar) => <MenuItem value={pillar.name} key={pillar.name}>{pillar.name}</MenuItem>)}
            </TextField>
            <TextField fullWidth type="time" label="Horario (opcional)" InputLabelProps={{ shrink: true }} value={form.time} onChange={(event) => setForm({ ...form, time: event.target.value })} />
            <TextField select fullWidth label="Estado" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
              {STATUSES.map((status) => <MenuItem value={status.value} key={status.value}>{status.label}</MenuItem>)}
            </TextField>
            <TextField fullWidth multiline rows={3} label="Notas (opcional)" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="Copy, referencia, enlace..." />
            <Chip label={`${activeChannel.name} · ${form.day ? new Date(`${form.day}T12:00:00`).toLocaleDateString("es-UY") : ""}`} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={saveEntry}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
}
